import { Box, Button, Typography } from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { useTheme } from '@mui/material/styles'
import { Fragment, useCallback, useEffect, useState } from 'react'
import type { FC, ReactElement, RefObject } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import qs from 'query-string'
import { format } from 'date-fns'
import invariant from 'tiny-invariant'

import type { ProvidersCasesCreateBodyParams } from '@fireflyhealth/api'
import type { PatientUser } from '~/shared/types'
import {
  isCarePlanTodo,
  isCaseTodo,
  usePersonTodos,
  type PatientTodoTabs,
  type RawTodoResult,
} from './PatientToDoService'
import {
  CaseCategorySelect,
  mapDraftToTaskBody,
  useAddCase,
  usePersonCaseCategories,
  usePersonCaseCategoriesById,
  useTaskCollectionPreviewMutation,
  workunitCompleteStates,
} from '~/entities/case'
import type { TaskFormDraft } from '~/entities/task'
import { queueNotification } from '~/shared/notifications/actions'
import { ISO_DATE_FORMAT, setDueDateToEOD } from '~/shared/lib/date'
import { useManageSelectedTodo } from '~/features/member-profile/communication'
import { CollapsibleSection, Loader, SectionCard, SegmentedControl } from '~/shared/ui'
import ToDoHeader from './ToDoHeader'
import ToDoNotes from './ToDoNotes'
import ToDoTasks from './ToDoTasks'
import { EmbeddedRelations } from './EmbeddedRelations'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import { logger } from '~/shared/lib/logger'
import { fireAndForget } from '~/shared/lib/fireAndForget'
import { sortedEntries } from '~/shared/lib/sortedEntries'

const TODO_TABS: Array<{ value: PatientTodoTabs; label: string }> = [
  { value: 'active', label: 'Open' },
  { value: 'complete', label: 'Closed' },
]

const MAX_CASE_SUMMARY_CONTENT_LENGTH = 400

// Design-refresh sibling of PatientTodoTaskComponents: the same expanded-case
// body (chat summary, notes, tasks, embedded relations) with the summary box
// and task grid restyled; notes and relations are reused untouched.
const CaseBodyRefresh: FC<{
  patient: PatientUser
  parentId: number
  todo: RawTodoResult
  isClosed: boolean
  setDisableEdit: (value: boolean) => void
  setIsTodoTaskUpdating: (value: boolean) => void
  setIsTodoNoteUpdating: (value: boolean) => void
}> = ({
  patient,
  parentId,
  todo,
  isClosed,
  setDisableEdit,
  setIsTodoTaskUpdating,
  setIsTodoNoteUpdating,
}) => {
  const caseContent = isCaseTodo(todo) ? todo.content_object : null
  const showCaseSummary = !!caseContent?.current_summary?.id

  return (
    <>
      {showCaseSummary && (
        <Box
          sx={theme => ({
            display: 'flex',
            gap: 1.25,
            p: 1.5,
            mb: 1.5,
            borderRadius: `${theme.shape.borderRadius}px`,
            bgcolor: 'background.external',
          })}
        >
          <AutoAwesomeRoundedIcon
            sx={{ fontSize: '1.8rem', color: 'secondary.dark', flex: '0 0 auto' }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '1.05rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'text.secondary',
                mb: 0.25,
              }}
            >
              Chat Summary
            </Typography>
            <Typography sx={{ fontSize: '1.3rem', color: 'text.secondary', lineHeight: 1.5 }}>
              {(() => {
                logger.info('[Chat Summary] for case Id', caseContent?.id)
                const summaryContent = caseContent?.current_summary?.content
                return summaryContent && summaryContent.length > MAX_CASE_SUMMARY_CONTENT_LENGTH
                  ? 'Summary withheld due to length'
                  : summaryContent
              })()}
            </Typography>
          </Box>
        </Box>
      )}
      <ToDoNotes
        patientTodo={todo}
        isClosed={isClosed}
        setIsTodoNoteUpdating={setIsTodoNoteUpdating}
      />
      <ToDoTasks
        patient={patient}
        parentId={parentId}
        todoContent={todo}
        isClosed={isClosed}
        setDisableEdit={setDisableEdit}
        setIsTodoTaskUpdating={setIsTodoTaskUpdating}
      />
      <EmbeddedRelations todo={todo} />
    </>
  )
}

// Same per-card updating flags ToDoItem holds; module-level so re-renders of
// the list never remount the card subtree.
const ToDoCardRefresh: FC<{
  parentId: number
  todoContent: RawTodoResult
  patient: PatientUser
  expandCase: boolean
  isClosed: boolean
  disableEdit: boolean
  setDisableEdit: (value: boolean) => void
  parentCategory: string
  isSelected: boolean
}> = props => {
  const [isTodoTaskUpdating, setIsTodoTaskUpdating] = useState<boolean>(false)
  const [isTodoNoteUpdating, setIsTodoNoteUpdating] = useState<boolean>(false)

  const theme = useTheme()
  // Left-accent color encodes the case's state, with selection taking priority:
  //   selected (open or closed) → primary blue (in focus; header tints yellow)
  //   open, not selected        → dark grey (active work)
  //   closed, not selected      → disabled grey (done; recedes)
  const accentColor = props.isSelected
    ? theme.palette.primary.main
    : props.isClosed
      ? theme.palette.action.disabled
      : theme.palette.grey[500]
  return (
    <SectionCard
      disablePadding
      sx={{
        '& + &': { mt: 1.25 },
        // The left edge carries the state accent above; every other edge keeps
        // SectionCard's default divider border (no extra outline needed now
        // that every case shows an accent bar).
        borderLeft: `3px solid ${accentColor}`,
        // The reused case accordion paints MUI's gray action.focus film over
        // the whole summary while a field inside it has focus. Legacy killed
        // it from the category Accordion's sx chain, which this shell
        // replaced with CollapsibleSection — neutralize it here instead.
        '& .MuiAccordionSummary-root.Mui-focusVisible': { backgroundColor: 'transparent' },
      }}
    >
      <ToDoHeader
        parentId={props.parentId}
        todoContent={props.todoContent}
        patient={props.patient}
        expandCase={props.expandCase}
        isClosed={props.isClosed}
        disableEdit={props.disableEdit}
        parentCategory={props.parentCategory}
        isTodoTaskUpdating={isTodoTaskUpdating}
        isTodoNoteUpdating={isTodoNoteUpdating}
      >
        <CaseBodyRefresh
          parentId={props.parentId}
          patient={props.patient}
          todo={props.todoContent}
          isClosed={props.isClosed}
          setDisableEdit={props.setDisableEdit}
          setIsTodoTaskUpdating={setIsTodoTaskUpdating}
          setIsTodoNoteUpdating={setIsTodoNoteUpdating}
        />
      </ToDoHeader>
    </SectionCard>
  )
}

/**
 * Design-refresh To Dos tab: Open/Closed segmented control, collapsible
 * category groups, and tone-accented case cards. Same data surface and
 * interactions as the legacy PatientCategorizedToDos — identical hooks,
 * grouping, deep-link expansion, scroll paging, and add-case flow. The case
 * header (ToDoHeader) is reused as-is; the expanded body renders through
 * CaseBodyRefresh (restyled summary box + task grid, reused notes/relations).
 * (The legacy "Add New Case" FFModal is not carried over: its `modalOpen`
 * state is never set, so the inline category select is the only live path.)
 */
const PatientCategorizedTodos = (props: {
  patient: PatientUser
  scrollRef?: RefObject<HTMLDivElement | null>
  scrollEventTarget?: EventTarget
}) => {
  const { patient, scrollRef, scrollEventTarget } = props

  const me = useSelector(state => state.me)

  const { result: categoriesAvailableForCreation, isLoading: isListingCaseCategories } =
    usePersonCaseCategories(patient.person.id)

  const [addCaseClick, setAddCaseClick] = useState<boolean>(false)
  const { navigateToCase } = useManageSelectedTodo()

  const {
    data: patientTodos,
    tab,
    setTab,
    hasNextPage,
    fetchNextPage,
    hasPreviousPage,
    fetchPreviousPage,
    goTo,
    isLoading: isLoadingTodos,
    isFetchingNextPage,
    isFetchingPreviousPage,
    resetFilters,
  } = usePersonTodos(patient.person.id)

  // Full unfiltered catalog for grouping and titles (system-only categories
  // still need to resolve a parent group even though staff can't create them).
  const { result: allCaseCategoriesById } = usePersonCaseCategoriesById(null)

  const parentCategorizedPatientTodos = patientTodos.reduce(
    (acc, patientTodo) => {
      let key
      if (isCarePlanTodo(patientTodo)) {
        key = 'Care Plan'
      } else if (isCaseTodo(patientTodo) && patientTodo.content_object) {
        if (patientTodo.content_object.category) {
          key = allCaseCategoriesById[patientTodo.content_object.category]?.parent
          if (!key) {
            key = 'Others'
          }
        }
      }
      if (key) {
        const group = acc[key] ?? []
        group.push(patientTodo)
        acc[key] = group
      }

      return acc
    },
    {} as Record<string, (typeof patientTodos)[number][]>,
  )

  const sortedParentCategories: typeof parentCategorizedPatientTodos = Object.fromEntries(
    sortedEntries(parentCategorizedPatientTodos),
  )

  const { isPending: isAddingPatientCase, mutateAsync: handleAddPatientCase } = useAddCase()

  const isLoading = isLoadingTodos || isListingCaseCategories || isAddingPatientCase

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [disableEdit, setDisableEdit] = useState<boolean>(false)

  const clearHistory = () => {
    const existingQS = qs.parse(location.search)
    delete existingQS['caseId']
    delete existingQS['careplanId']
    // Pass the pathname explicitly: a pathless partial resolves against the
    // matched route, not the current URL, and would drop splat segments.
    void navigate({
      pathname: location.pathname,
      search: qs.stringify(existingQS),
    })
  }

  const escFunction = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setAddCaseClick(false)
      }
    },
    [setAddCaseClick],
  )

  const taskCollectionPreviewMutation = useTaskCollectionPreviewMutation()
  const dueDate = format(new Date(), ISO_DATE_FORMAT)

  const handleAddCase = async (category: (typeof categoriesAvailableForCreation)[0]) => {
    if (category) {
      let tasks: TaskFormDraft[] = []
      if (category?.task_collection_id) {
        invariant(me.assignee_group !== null, 'Expected me.assignee_group to be not null')

        const taskData = await taskCollectionPreviewMutation.mutateAsync({
          caseCategoryId: category.id,
          userId: patient.id,
          meAssigneeGroup: me.assignee_group.id,
        })

        if (taskData) tasks = taskData.taskPreviews
      }

      const body: ProvidersCasesCreateBodyParams = {
        person: patient.person.id,
        category: category.id,
        tasks: tasks.map(task => mapDraftToTaskBody(task, setDueDateToEOD(task.due_date))),
        relations: [],
        description: category?.description || '',
        notes: category?.notes || '',
        due_date: dueDate != '' ? setDueDateToEOD(dueDate) : undefined,
      }
      const addedCase = await handleAddPatientCase(body)

      handleOnAfterAddCase(addedCase)
    }
  }

  const { selectedCaseId, selectedCarePlanId, forwardDraftCaseId } = useManageSelectedTodo()
  const expandCase = selectedCaseId ? true : false
  useEffect(() => {
    document.addEventListener('keydown', escFunction, false)

    if (!selectedCaseId && !selectedCarePlanId) {
      resetFilters()
      return
    }

    if (selectedCarePlanId) {
      goTo({ contentType: 'careplan', objectId: selectedCarePlanId })
    } else if (selectedCaseId) {
      goTo({ contentType: 'case', objectId: selectedCaseId })
    }

    return () => {
      document.removeEventListener('keydown', escFunction, false)
    }
    // resetFilters/goTo are useCallback-stable in usePersonTodos, so the full
    // list is safe (unlike the suppressed legacy counterpart).
  }, [selectedCaseId, selectedCarePlanId, goTo, escFunction, resetFilters])

  const expandParentCategory: string[] = []
  const toDosHTML: Record<string, ReactElement[]> = {}
  const countPatientTodo: Record<string, number> = {}

  for (const [key, value] of Object.entries<RawTodoResult[]>(sortedParentCategories)) {
    countPatientTodo[key] = 0
    for (const patientTodo of value) {
      countPatientTodo[key] = countPatientTodo[key] + 1
      const parentId = patientTodo.object_id
      const isClosed =
        isCaseTodo(patientTodo) &&
        workunitCompleteStates.includes(patientTodo.content_object?.status_category)

      if (
        (selectedCarePlanId && selectedCarePlanId === parentId) ||
        (selectedCaseId && selectedCaseId === parentId) ||
        (forwardDraftCaseId && forwardDraftCaseId === parentId)
      )
        expandParentCategory.push(key)

      // The navigated case/care plan (the one the header highlights yellow)
      // gets the blue left bar; forward-draft targets do not, matching the
      // highlight.
      const isSelected = Boolean(
        (selectedCaseId && selectedCaseId === parentId) ||
        (selectedCarePlanId && selectedCarePlanId === parentId),
      )

      toDosHTML[key] ||= []
      toDosHTML[key].push(
        <ToDoCardRefresh
          key={`ToDoCard_${key}_${patientTodo.content_type}_${parentId}`}
          parentId={parentId}
          todoContent={patientTodo}
          patient={patient}
          expandCase={expandCase ?? false}
          isClosed={isClosed ?? false}
          disableEdit={disableEdit ?? false}
          parentCategory={key}
          setDisableEdit={setDisableEdit}
          isSelected={isSelected}
        />,
      )
    }
  }

  const [accordionState, _setAccordionState] = useState<Record<string, boolean>>(
    expandParentCategory.reduce((acc, key) => ({ ...acc, [key]: true }), {}),
  )
  const setAccordionState = (key: string, value: boolean) =>
    _setAccordionState({ ...accordionState, [key]: value })

  // Force-open the groups holding a deep-linked case whenever that set of groups changes.
  const expandParentCategoryKey = expandParentCategory.join(',')
  const [expandedForKey, setExpandedForKey] = useState(expandParentCategoryKey)
  if (expandParentCategoryKey !== expandedForKey) {
    setExpandedForKey(expandParentCategoryKey)
    if (expandParentCategory.length > 0) {
      _setAccordionState(previous =>
        expandParentCategory.reduce((acc, key) => ({ ...acc, [key]: true }), previous),
      )
    }
  }

  const handleScroll = useCallback(() => {
    if (!scrollRef) {
      return
    }

    const element = scrollRef.current
    if (!element) {
      return
    }

    const atBottom = element.scrollTop + element.clientHeight >= element.scrollHeight
    const atTop = element.scrollTop === 0

    if (atTop && hasPreviousPage) {
      void fetchPreviousPage()
    } else if (atBottom && hasNextPage) {
      void fetchNextPage()
    }
  }, [scrollRef, hasNextPage, fetchNextPage, hasPreviousPage, fetchPreviousPage])

  const handleOnAfterAddCase = (newCase: { id: number }) => {
    if (newCase) {
      navigateToCase(newCase.id, { type: 'add' })
    } else {
      dispatch(
        queueNotification({
          variant: 'error',
          message: 'Case Creation Failed',
        }),
      )
    }
    setAddCaseClick(false)
  }

  useEffect(() => {
    scrollEventTarget?.addEventListener('scroll', handleScroll)

    return () => {
      scrollEventTarget?.removeEventListener('scroll', handleScroll)
    }
  }, [scrollEventTarget, handleScroll])

  return (
    <Box sx={{ px: 3, pt: 1.5, pb: 5 }}>
      {isLoading && <Loader />}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <SegmentedControl
          options={TODO_TABS}
          value={tab}
          onChange={nextTab => {
            clearHistory()
            setTab(nextTab)
          }}
          aria-label="Case state"
        />
        {/* Fixed-size container: the button and the category select swap
            inside it without changing the row's width or height. */}
        <Box
          sx={{
            ml: 'auto',
            width: '34%',
            minWidth: 220,
            minHeight: '4.2rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          {addCaseClick ? (
            <Box sx={{ width: '100%' }}>
              <CaseCategorySelect
                categories={categoriesAvailableForCreation}
                category={null}
                handleAddCase={category =>
                  fireAndForget('[Todos] add case', () => handleAddCase(category))
                }
                setCategory={() => {}}
                freeSolo={true}
                onBlur={() => setAddCaseClick(false)}
              />
            </Box>
          ) : (
            <Button
              variant="text"
              color="primary"
              size="small"
              startIcon={<AddRoundedIcon />}
              onClick={() => setAddCaseClick(true)}
              sx={{ fontSize: '1.4rem', fontWeight: 500 }}
            >
              Add case
            </Button>
          )}
        </Box>
      </Box>

      {isFetchingPreviousPage && <LoadingMoreRefresh />}
      <Fragment key={tab}>
        {Object.keys(sortedParentCategories).map(key => (
          <Box key={key} sx={{ mb: 2 }}>
            <CollapsibleSection
              label={key}
              count={countPatientTodo[key]}
              // Groups closed by default (legacy parity — deliberate ops
              // preference over the mock's open-default); deep-linked cases
              // force their group open via accordionState.
              open={!!accordionState[key]}
              onToggle={open => setAccordionState(key, open)}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {toDosHTML[key]}
              </Box>
            </CollapsibleSection>
          </Box>
        ))}
      </Fragment>
      {isFetchingNextPage && <LoadingMoreRefresh />}
    </Box>
  )
}

const LoadingMoreRefresh = () => (
  <Box sx={{ pl: 2, pt: 2, display: 'flex', alignItems: 'center' }}>
    <Typography sx={{ fontSize: '1.3rem', color: 'text.secondary' }}>Loading more...</Typography>
  </Box>
)

export default PatientCategorizedTodos
